import { useCallback } from 'react';

import { Tooltip, styled } from '@material-ui/core';

import { RiArrowDownLine, RiArrowUpLine } from '@remixicon/react';
import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';

import {
  TopLevelBlockInfo,
  findTopLevelBlockInfo,
  findTopLevelBlockInfoByPos,
  swapWithNextBlock,
  swapWithPreviousBlock,
} from '../../utils/blockReordering';
import { useCurrentEditorOrNull } from '../Context';
import { ToolbarButton } from '../ToolbarButton';

const Container = styled('div')({
  borderRight: '1px solid var(--decorative-subdued)',
  display: 'flex',
  marginRight: 'var(--encore-spacing-tighter)',
  paddingRight: 'var(--encore-spacing-tighter-4)',
});

type BlockReorderButtonsProps = {
  getPos?: () => number | undefined;
  // Editor instance - required for NodeView contexts (outside EditorProvider).
  // If not provided, will use useCurrentEditor() for BubbleMenu contexts.
  editor?: Editor;
};

type BlockReorderState = Pick<
  TopLevelBlockInfo,
  'blockIndex' | 'isFirst' | 'isLast' | 'pos'
>;

export const BlockReorderButtons = ({
  getPos,
  editor: editorProp,
}: BlockReorderButtonsProps = {}) => {
  // Use provided editor (NodeView context) or fall back to context editor (BubbleMenu context)
  const editorFromContext = useCurrentEditorOrNull();
  const editor = editorProp ?? editorFromContext.editor;

  const blockInfo = useEditorState({
    editor,
    selector: (ctx): BlockReorderState | null => {
      if (!ctx.editor) return null;

      // For NodeView components, use the explicit position
      if (getPos) {
        const pos = getPos();
        if (typeof pos !== 'number') return null;
        const block = findTopLevelBlockInfoByPos(ctx.editor.state, pos);
        if (!block) return null;
        const { blockIndex, isFirst, isLast, pos: blockPos } = block;
        return { blockIndex, isFirst, isLast, pos: blockPos };
      }

      // For bubble menu toolbars, use selection-based positioning
      const block = findTopLevelBlockInfo(ctx.editor.state);
      if (!block) return null;
      const { blockIndex, isFirst, isLast, pos } = block;
      return { blockIndex, isFirst, isLast, pos };
    },
  });

  const handleMoveUp = useCallback(() => {
    if (!editor || !blockInfo) return;
    const currentBlockInfo =
      findTopLevelBlockInfoByPos(editor.state, blockInfo.pos) ??
      findTopLevelBlockInfo(editor.state);
    if (!currentBlockInfo) return;
    swapWithPreviousBlock(
      editor,
      currentBlockInfo.pos,
      currentBlockInfo.node,
      currentBlockInfo.blockIndex,
    );
  }, [editor, blockInfo]);

  const handleMoveDown = useCallback(() => {
    if (!editor || !blockInfo) return;
    const currentBlockInfo =
      findTopLevelBlockInfoByPos(editor.state, blockInfo.pos) ??
      findTopLevelBlockInfo(editor.state);
    if (!currentBlockInfo) return;
    swapWithNextBlock(
      editor,
      currentBlockInfo.pos,
      currentBlockInfo.node,
      currentBlockInfo.blockIndex,
    );
  }, [editor, blockInfo]);

  // Don't render buttons for nested NodeViews (e.g., images inside cards)
  // where blockInfo is null because the position doesn't resolve to a top-level block
  if (!blockInfo) {
    return null;
  }

  return (
    <Container>
      <Tooltip title="Move block up">
        <span>
          <ToolbarButton
            aria-label="Move block up"
            onClick={handleMoveUp}
            disabled={blockInfo.isFirst}
          >
            <RiArrowUpLine />
          </ToolbarButton>
        </span>
      </Tooltip>
      <Tooltip title="Move block down">
        <span>
          <ToolbarButton
            aria-label="Move block down"
            onClick={handleMoveDown}
            disabled={blockInfo.isLast}
          >
            <RiArrowDownLine />
          </ToolbarButton>
        </span>
      </Tooltip>
    </Container>
  );
};
